import { useEffect, useMemo } from 'react';

import { useIsLoggingIn } from '@order/AuthMachine';
import { useBraze } from '@order/Braze';
import { useCustomer } from '@order/Customer';
import { useLaunchDarkly } from '@order/LaunchDarkly';
import { useOneSignal } from '@order/OneSignal';
import { useTelemetry } from '@order/Telemetry';

import {
  init as initFullStory,
  resetCustomer as resetFullStoryCustomer,
  syncCustomer as syncFullStoryCustomer,
} from '../../integrations/fullstory';
import { useLogoutKustomer } from '../../integrations/kustomer';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Initializes third-party vendors integrations and synchronizes current customer data
 */
export const use3rdPartyIntegrations = () => {
  const isLoggingIn = useIsLoggingIn();
  const { customer: customerData } = useCustomer();
  const { id, trackingUuid, firstName, lastName, email } = customerData;

  // extract required customer data for 3rd party vendors to prevent extra effects
  const customer = useMemo(
    () => ({ id, trackingUuid, firstName, lastName, email }),
    [id, trackingUuid, firstName, lastName, email],
  );

  // ─────────────────────────────────────────────────────────────────

  // extract helpers individually to prevent unnecessary effects because of internal state updates

  const {
    initialize: initTelemetry,
    identify: syncTelemetryCustomer,
    reset: resetTelemetryCustomer,
  } = useTelemetry();
  const {
    isReady: isLaunchDarklyReady,
    init,
    syncCustomer: syncLaunchDarklyCustomer,
    resetCustomer: resetLaunchDarklyCustomer,
  } = useLaunchDarkly();
  const {
    init: initOneSignal,
    syncCustomer: syncOneSignalCustomer,
    resetCustomer: resetOneSignalCustomer,
  } = useOneSignal();
  const {
    init: initBraze,
    destroy: destroyBraze,
    syncCustomer: syncBrazeCustomer,
  } = useBraze();
  const resetKustomerUser = useLogoutKustomer();

  // ─── Launch Darkly ───────────────────────────────────────────────────

  useEffect(() => {
    if (isLaunchDarklyReady || isLoggingIn) return;

    void init(customer);
  }, [customer, init, isLoggingIn, isLaunchDarklyReady]);

  useEffect(() => {
    if (!isLaunchDarklyReady || !customer.id) return;

    void syncLaunchDarklyCustomer(customer);

    return () => {
      void resetLaunchDarklyCustomer();
    };
  }, [
    customer,
    syncLaunchDarklyCustomer,
    resetLaunchDarklyCustomer,
    isLaunchDarklyReady,
  ]);

  // ─── One Signal ──────────────────────────────────────────────────────

  useEffect(() => {
    initOneSignal();
  }, [initOneSignal]);

  useEffect(() => {
    if (!customer.id) return;

    syncOneSignalCustomer(customer);

    return () => {
      resetOneSignalCustomer();
    };
  }, [customer, syncOneSignalCustomer, resetOneSignalCustomer]);

  // ─── Braze ───────────────────────────────────────────────────────────

  useEffect(() => {
    initBraze?.();

    return () => {
      destroyBraze?.();
    };
  }, [destroyBraze, initBraze]);

  useEffect(() => {
    if (!customer.id) return;

    syncBrazeCustomer(customer);
  }, [customer, syncBrazeCustomer]);

  // ─── Full Story ──────────────────────────────────────────────────────

  useEffect(() => {
    void initFullStory();
  }, []);

  useEffect(() => {
    if (!customer.id) return;

    syncFullStoryCustomer(customer);

    return () => {
      resetFullStoryCustomer();
    };
  }, [customer]);

  // ─── Kustomer ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!customer.id) return;

    return () => {
      resetKustomerUser();
    };
  }, [customer, resetKustomerUser]);

  // ─── Telemetry ───────────────────────────────────────────────────────

  useEffect(() => {
    void initTelemetry();
  }, [initTelemetry]);

  useEffect(() => {
    if (!customer.id) return;

    syncTelemetryCustomer(customer);

    return () => {
      resetTelemetryCustomer();
    };
  }, [customer, resetTelemetryCustomer, syncTelemetryCustomer]);
};
